/**
 * First-touch marketing attribution.
 *
 * Captured once per browser session on the first page the visitor lands on:
 * the `utm_*` query parameters (LinkedIn/Google ads, email links), the HTTP
 * referrer, and the landing path. Stored in sessionStorage so it survives the
 * SPA navigation from the ad landing page to the editor and on to the quote
 * form, then sent as `attribution` on POST /api/quotes.
 *
 * Why session-scoped and first-touch: the question the ad report could not
 * answer was "which channel produced this quote". A later in-session URL
 * without UTMs must not overwrite the campaign that brought the visitor in.
 *
 * No PII is captured: only campaign tags, the referring ORIGIN (never its path
 * or query), and the landing path on our own site. Values are length-capped here and again on the server.
 */

export const ATTRIBUTION_STORAGE_KEY = 'fcb.attribution.v1';
export const ATTRIBUTION_VALUE_MAX = 120;

export interface Attribution {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  referrer?: string;
  landingPath?: string;
}

const UTM_TO_KEY: ReadonlyArray<[string, keyof Attribution]> = [
  ['utm_source', 'source'],
  ['utm_medium', 'medium'],
  ['utm_campaign', 'campaign'],
  ['utm_content', 'content'],
  ['utm_term', 'term'],
];

/** Minimal storage surface so tests and callers can inject a fake. */
export interface AttributionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): AttributionStorage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    // Safari private mode / storage disabled throws on access.
    return null;
  }
}

function clip(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, ATTRIBUTION_VALUE_MAX);
}

/**
 * Reduce a referrer URL to its ORIGIN only. The path can identify a person
 * (a LinkedIn profile URL, a forum thread), the query can carry someone else's
 * tracking IDs, and neither is needed: "which site sent them" is the question,
 * and campaign detail comes from the utm_* parameters we control.
 */
export function normalizeReferrer(referrer: string | null | undefined, ownOrigin?: string): string | undefined {
  if (!referrer) return undefined;
  try {
    const url = new URL(referrer);
    if (ownOrigin && url.origin === ownOrigin) return undefined;
    return clip(url.origin);
  } catch {
    return undefined;
  }
}

/**
 * Build the attribution record from a landing location. Returns null when the
 * landing carries nothing worth recording (no UTMs and no external referrer):
 * a direct visit to `/` is not attribution, it is the absence of it.
 */
export function buildAttribution(
  location: { search: string; pathname: string; origin?: string },
  referrer: string | null | undefined,
): Attribution | null {
  const params = new URLSearchParams(location.search);
  const out: Attribution = {};
  for (const [param, key] of UTM_TO_KEY) {
    const value = clip(params.get(param));
    if (value) out[key] = value;
  }
  const ref = normalizeReferrer(referrer, location.origin);
  if (ref) out.referrer = ref;
  if (!Object.keys(out).length) return null;
  out.landingPath = clip(location.pathname) ?? '/';
  return out;
}

/**
 * Capture first-touch attribution for this session. Idempotent: a value
 * already in storage is never overwritten, so the first landing wins.
 * Returns the stored record (existing or newly written), or null.
 */
export function captureAttribution(
  location: { search: string; pathname: string; origin?: string },
  referrer: string | null | undefined,
  storage: AttributionStorage | null = defaultStorage(),
): Attribution | null {
  const existing = readAttribution(storage);
  if (existing) return existing;
  const built = buildAttribution(location, referrer);
  if (!built) return null;
  try {
    storage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(built));
  } catch {
    // Quota / disabled storage: attribution is best-effort, never blocks the page.
  }
  return built;
}

/** Read the stored first-touch record, or null when none / unreadable. */
export function readAttribution(storage: AttributionStorage | null = defaultStorage()): Attribution | null {
  try {
    const raw = storage?.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Attribution;
  } catch {
    return null;
  }
}
